import { useEffect, useRef, useState, type ReactNode } from "react";
import { drawPages } from "@/lib/pdfPages";

/**
 * A PDF's pages, drawn `width` CSS pixels wide and shown at the width of
 * their box. `most` keeps to the first pages. `placeholder` shows until
 * they are drawn, or in their place when they cannot be.
 */
export default function PdfPages({
    pdf,
    width,
    most,
    label,
    className = "",
    placeholder,
}: {
    pdf: Blob | undefined;
    width: number;
    most?: number;
    label?: string;
    className?: string;
    placeholder: ReactNode;
}) {
    const box = useRef<HTMLDivElement>(null);
    const [drawn, setDrawn] = useState(false);
    useEffect(() => {
        const target = box.current;
        if (!pdf || !target) return;
        const stop = new AbortController();
        drawPages(pdf, width, stop.signal, most).then(
            (canvases) => {
                if (stop.signal.aborted || canvases.length === 0) return;
                for (const canvas of canvases) canvas.className = "block h-auto w-full bg-white";
                target.replaceChildren(...canvases);
                setDrawn(true);
            },
            () => {},
        );
        return () => {
            stop.abort();
            target.replaceChildren();
            setDrawn(false);
        };
    }, [pdf, width, most]);
    return (
        <>
            <div
                ref={box}
                role={label ? "img" : undefined}
                aria-label={label}
                aria-hidden={label ? undefined : true}
                className={`${className} ${drawn ? "" : "hidden"}`}
            />
            {!drawn && placeholder}
        </>
    );
}
